import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { formatNaira } from "@/lib/mock-data";
import { walletApi, payoutRequestsApi, type Wallet as WalletDto, type WalletTransaction, type PayoutRequest, ApiError } from "@/lib/api";
import { TopupDialog } from "@/components/parcelio/topup-dialog";
import { PayoutDialog } from "@/components/parcelio/payout-dialog";
import { ArrowDownLeft, ArrowUpRight, Plus, Send, Wallet as WalletIcon, Check, X, Banknote } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

function formatKobo(amountKobo: number) {
  return formatNaira(amountKobo / 100);
}

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

export default function Wallet() {
  const session = useAuth((s) => s.session);
  const token = session?.accessToken;
  const isStaff = session?.kind === "staff";
  const roles = session?.roles ?? [];
  const isDriverOnly = isStaff && roles.length > 0 && roles.every((r) => r === "driver");
  const canReviewPayouts = isStaff && !isDriverOnly;
  const [wallet, setWallet] = useState<WalletDto | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [topupOpen, setTopupOpen] = useState(false);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([walletApi.get(undefined, token), walletApi.transactions(undefined, token)])
      .then(([w, txns]) => {
        setWallet(w);
        setTransactions(txns);
      })
      .catch(() => toast.error("Failed to load wallet"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const loadPayoutRequests = () => {
    if (!canReviewPayouts) return;
    setLoadingRequests(true);
    payoutRequestsApi
      .list(token)
      .then(setPayoutRequests)
      .catch(() => toast.error("Failed to load payout requests"))
      .finally(() => setLoadingRequests(false));
  };

  useEffect(loadPayoutRequests, [canReviewPayouts]);

  const reviewRequest = async (id: string, status: "approved" | "rejected" | "paid") => {
    setReviewingId(id);
    try {
      await payoutRequestsApi.review(id, status, undefined, token);
      toast.success(`Request ${status}`);
      loadPayoutRequests();
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not update request");
    } finally {
      setReviewingId(null);
    }
  };

  const recent = transactions.slice(0, 6);
  const pendingRequests = payoutRequests.filter((r) => r.status === "pending" || r.status === "approved");

  return (
    <>
      <Topbar
        title="Wallet"
        subtitle={isStaff ? "Platform-wide balances — payouts require review" : "Balances, top-ups and payouts"}
        actions={
          isStaff ? undefined : (
            <>
              <Button variant="outline" size="sm" onClick={() => setPayoutOpen(true)}><Send className="mr-1.5 h-4 w-4" /> Payout</Button>
              <Button size="sm" onClick={() => setTopupOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Top up</Button>
            </>
          )
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        {/* Balance hero card */}
        <div className="relative overflow-hidden rounded-2xl bg-primary p-6 text-primary-foreground md:p-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-success/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-info/20 blur-3xl" />
          <div className="relative grid gap-6 md:grid-cols-3">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary-foreground/70">
                <WalletIcon className="h-3.5 w-3.5" /> {isStaff ? "Platform available balance" : "Available balance"}
              </div>
              <div className="mt-2 text-4xl font-bold tracking-tight">
                {loading || !wallet ? "…" : formatKobo(wallet.available_kobo)}
              </div>
              <div className="mt-1 text-xs text-primary-foreground/70">Updated just now</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-primary-foreground/70">Pending payout</div>
              <div className="mt-2 text-2xl font-semibold">
                {loading || !wallet ? "…" : formatKobo(wallet.pending_kobo)}
              </div>
              <div className="mt-1 text-xs text-primary-foreground/70">Reserved, awaiting approval/transfer</div>
            </div>
            <div className="flex flex-col justify-end gap-2 md:items-end">
              {isStaff ? null : (
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setTopupOpen(true)}><Plus className="mr-1.5 h-4 w-4" /> Top up</Button>
                  <Button variant="outline" size="sm" className="border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10" onClick={() => setPayoutOpen(true)}>
                    <Send className="mr-1.5 h-4 w-4" /> Payout
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-primary-foreground/70">
                <span>Paystack</span><span>·</span><span>Stripe</span>
              </div>
            </div>
          </div>
        </div>

        {/* Admin: payout requests queue. Everyone else has no saved-card UI
            to show here — top-ups go straight through the provider widget
            and payout accounts are managed inline inside the Payout dialog. */}
        {canReviewPayouts ? (
          <section className="surface-card">
            <div className="flex items-center justify-between border-b p-4">
              <div>
                <h3 className="text-sm font-semibold">Payout requests</h3>
                <p className="text-xs text-muted-foreground">Approve, reject, or mark paid after transferring funds manually — nothing here moves money automatically.</p>
              </div>
              <Banknote className="h-5 w-5 text-muted-foreground" />
            </div>
            <ul className="divide-y">
              {loadingRequests ? <li><LoadingBlock label="Loading requests…" /></li> : null}
              {!loadingRequests && pendingRequests.length === 0 ? (
                <li className="p-4 text-sm text-muted-foreground">No payout requests awaiting action.</li>
              ) : null}
              {!loadingRequests && pendingRequests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{r.requester_name ?? "Unknown"} · {formatKobo(r.amount_kobo)}</div>
                    <div className="text-xs text-muted-foreground">{r.bank_name} · {r.account_number} ({r.account_name})</div>
                    <div className="text-[10px] text-muted-foreground">{formatDateTime(r.created_at)}</div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span className={cn(
                      "rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                      r.status === "pending" && "border-warning/30 bg-warning/15 text-warning-foreground",
                      r.status === "approved" && "border-info/30 bg-info/10 text-info"
                    )}>
                      {r.status}
                    </span>
                    {r.status === "pending" ? (
                      <>
                        <Button size="sm" variant="outline" disabled={reviewingId === r.id} onClick={() => reviewRequest(r.id, "approved")}>
                          <Check className="mr-1 h-3.5 w-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" disabled={reviewingId === r.id} onClick={() => reviewRequest(r.id, "rejected")}>
                          <X className="mr-1 h-3.5 w-3.5" /> Reject
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" disabled={reviewingId === r.id} onClick={() => reviewRequest(r.id, "paid")}>
                        Mark paid
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Recent transactions */}
        <section className="surface-card">
          <div className="flex items-center justify-between border-b p-4">
            <h3 className="text-sm font-semibold">Recent activity</h3>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/app/transactions">View all <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          <ul className="divide-y">
            {loading ? (
              <li><LoadingBlock label="Loading activity…" /></li>
            ) : recent.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">No recent activity.</li>
            ) : (
              recent.map((t) => {
                const inflow = t.amount_kobo >= 0;
                return (
                  <li key={t.id} className="flex items-center gap-3 p-4">
                    <div
                      className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-full",
                        inflow ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                      )}
                    >
                      {inflow ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {typeLabel(t.type)} · <span className="font-mono text-xs">{t.reference}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(t.occurred_at)} · {t.method ?? t.provider}
                      </div>
                    </div>
                    <div className={cn("text-sm font-semibold", inflow ? "text-success" : "text-foreground")}>
                      {formatKobo(t.amount_kobo)}
                    </div>
                    <StatusChip status={t.status} />
                  </li>
                );
              })
            )}
          </ul>
        </section>
      </div>

      {isStaff ? null : (
        <>
          <TopupDialog
            open={topupOpen}
            onOpenChange={setTopupOpen}
            token={token}
            onSuccess={(w) => {
              setWallet(w);
              load();
            }}
          />
          <PayoutDialog
            open={payoutOpen}
            onOpenChange={setPayoutOpen}
            token={token}
            wallet={wallet}
            onSuccess={load}
          />
        </>
      )}
    </>
  );
}

function typeLabel(t: string) {
  return { topup: "Wallet top-up", payout: "Driver payout", delivery_fee: "Delivery fee", refund: "Refund" }[t as "topup"] ?? t;
}

function StatusChip({ status }: { status: "success" | "pending" | "failed" }) {
  const s = {
    success: "bg-success/10 text-success border-success/20",
    pending: "bg-warning/15 text-warning-foreground border-warning/30",
    failed: "bg-destructive/10 text-destructive border-destructive/20",
  }[status];
  return <span className={cn("hidden shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize sm:inline-flex", s)}>{status}</span>;
}
